import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  adminApi,
  type CreateCategoryDto,
  type CreateDisciplineDto,
} from '../../api/adminApi';
import { resultsApi } from '../../api/resultsApi';
import {
  AGE_GROUP_LABELS,
  DISCIPLINE_TYPE_LABELS,
  type AgeGroup,
  type Category,
  type Discipline,
  type DisciplineType,
  type TimeFormat,
} from '../../types/domain';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { StartListConfigTab } from './tabs/StartListConfigTab';
import { StartListTab } from './tabs/StartListTab';

interface Props {
  competitionId: number;
  categories: Category[];
}

export const CompetitionCategoriesTab = ({ competitionId, categories }: Props) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);
  const [discDialog, setDiscDialog] = useState<{ categoryId: number; discipline: Discipline | null } | null>(null);
  const [deletingDiscipline, setDeletingDiscipline] = useState<Discipline | null>(null);
  const [startListDialog, setStartListDialog] = useState<Discipline | null>(null);
  const [startListDialogTab, setStartListDialogTab] = useState<'config' | 'generated'>('config');
  const [activeCatIdx, setActiveCatIdx] = useState(0);

  useEffect(() => {
    if (activeCatIdx >= categories.length && categories.length > 0) {
      setActiveCatIdx(0);
    }
  }, [activeCatIdx, categories.length]);

  const activeCat = categories[activeCatIdx];

  // Nacist vsechny vysledky soutezi, abychom mohli zobrazit varovani pri mazani
  const { data: allResults = [] } = useQuery({
    queryKey: ['admin', 'results', competitionId],
    queryFn: () => resultsApi.listByCompetition(competitionId),
  });

  const countResultsForDiscipline = (disciplineId: number) =>
    allResults.filter((r) => r.disciplineId === disciplineId).length;

  const countResultsForCategory = (cat: Category) =>
    cat.disciplines.reduce((sum, d) => sum + countResultsForDiscipline(d.id), 0);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'competition', competitionId, 'categories'] });

  const handleError = (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' });

  const catCreate = useMutation({
    mutationFn: (dto: CreateCategoryDto) => adminApi.createCategory(competitionId, dto),
    onSuccess: () => {
      enqueueSnackbar('Kategorie vytvořena', { variant: 'success' });
      invalidate();
      setCreatingCategory(false);
    },
    onError: handleError,
  });

  const catUpdate = useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: CreateCategoryDto }) => adminApi.updateCategory(id, dto),
    onSuccess: () => {
      enqueueSnackbar('Kategorie uložena', { variant: 'success' });
      invalidate();
      setEditingCategory(null);
    },
    onError: handleError,
  });

  const catDelete = useMutation({
    mutationFn: (id: number) => adminApi.deleteCategory(id),
    onSuccess: () => {
      enqueueSnackbar('Kategorie smazána', { variant: 'success' });
      invalidate();
      setDeletingCategory(null);
    },
    onError: handleError,
  });

  const discSave = useMutation({
    mutationFn: ({ categoryId, id, dto }: { categoryId: number; id: number | null; dto: CreateDisciplineDto }) =>
      id ? adminApi.updateDiscipline(id, dto) : adminApi.createDiscipline(categoryId, dto),
    onSuccess: () => {
      enqueueSnackbar('Disciplína uložena', { variant: 'success' });
      invalidate();
      setDiscDialog(null);
    },
    onError: handleError,
  });

  const discDelete = useMutation({
    mutationFn: (id: number) => adminApi.deleteDiscipline(id),
    onSuccess: () => {
      enqueueSnackbar('Disciplína smazána', { variant: 'success' });
      invalidate();
      setDeletingDiscipline(null);
    },
    onError: handleError,
  });

  const discTimeFormat = useMutation({
    mutationFn: ({ id, timeFormat }: { id: number; timeFormat: TimeFormat }) =>
      adminApi.updateDisciplineTimeFormat(id, timeFormat),
    onSuccess: () => {
      enqueueSnackbar('Formát času uložen', { variant: 'success' });
      invalidate();
    },
    onError: handleError,
  });

  return (
    <>
      {categories.length === 0 ? (
        <Card>
          <CardContent>
            <Stack spacing={2} alignItems="flex-start">
              <Typography color="text.secondary">
                Žádné kategorie. Přidejte ručně nebo vytvořte soutěž ze šablony.
              </Typography>
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => setCreatingCategory(true)}
              >
                Nová kategorie
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tabs
              value={activeCatIdx}
              onChange={(_, v) => setActiveCatIdx(v)}
              variant={categories.length > 4 ? 'scrollable' : 'fullWidth'}
              scrollButtons="auto"
              sx={{
                flex: 1,
                '& .MuiTab-root': {
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  fontSize: '0.9375rem',
                  minHeight: 56,
                  px: 2,
                  flex: categories.length > 4 ? undefined : 1,
                },
              }}
            >
              {categories.map((c) => (
                <Tab key={c.id} label={c.name} />
              ))}
            </Tabs>
            <Tooltip title="Nová kategorie">
              <IconButton
                color="primary"
                onClick={() => setCreatingCategory(true)}
                sx={{ ml: 1 }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          {activeCat && (
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {AGE_GROUP_LABELS[activeCat.ageGroup]}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {activeCat.teamCount} družstev · {activeCat.disciplines.length} disciplín
                    </Typography>
                  </Box>
                  <Stack direction="row">
                    <Tooltip title="Upravit kategorii">
                      <IconButton size="small" onClick={() => setEditingCategory(activeCat)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Smazat kategorii">
                      <IconButton size="small" color="error" onClick={() => setDeletingCategory(activeCat)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>

                {activeCat.disciplines.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      Kategorie zatím nemá žádné disciplíny.
                    </Typography>
                  </Box>
                ) : (
                  <List dense>
                    {activeCat.disciplines.map((d) => (
                      <ListItem key={d.id} sx={{ pr: 26 }}>
                        <ListItemText
                          primary={d.name}
                          secondary={`${DISCIPLINE_TYPE_LABELS[d.type]} • ${d.attemptsCount}× pokus${d.attemptsCount > 1 ? 'y' : ''}`}
                        />
                        <ListItemSecondaryAction>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Tooltip title="Formát zobrazení času (board, veřejné výsledky)">
                              <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={d.timeFormat ?? 'MM_SS_CC'}
                                onChange={(_, next) => {
                                  if (next != null && next !== d.timeFormat) {
                                    discTimeFormat.mutate({ id: d.id, timeFormat: next as TimeFormat });
                                  }
                                }}
                                disabled={discTimeFormat.isPending}
                                aria-label="Formát zobrazení času"
                              >
                                <ToggleButton
                                  value="SS_CC"
                                  sx={{ px: 1, py: 0.25, fontSize: '0.7rem', textTransform: 'none', fontFamily: 'monospace' }}
                                >
                                  SS,cc
                                </ToggleButton>
                                <ToggleButton
                                  value="MM_SS_CC"
                                  sx={{ px: 1, py: 0.25, fontSize: '0.7rem', textTransform: 'none', fontFamily: 'monospace' }}
                                >
                                  M:SS,cc
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </Tooltip>
                            <Tooltip title="Konfigurovat startovací skupiny">
                              <IconButton size="small" onClick={() => setStartListDialog(d)}>
                                <FormatListNumberedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Upravit disciplínu">
                              <IconButton size="small" onClick={() => setDiscDialog({ categoryId: activeCat.id, discipline: d })}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Smazat disciplínu">
                              <IconButton size="small" color="error" onClick={() => setDeletingDiscipline(d)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}

                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setDiscDialog({ categoryId: activeCat.id, discipline: null })}
                  sx={{ mt: 1 }}
                >
                  Přidat disciplínu
                </Button>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* Category edit/create */}
      <CategoryDialog
        open={creatingCategory || !!editingCategory}
        category={editingCategory}
        loading={catCreate.isPending || catUpdate.isPending}
        onCancel={() => {
          setCreatingCategory(false);
          setEditingCategory(null);
        }}
        onSubmit={(dto) => {
          if (editingCategory) {
            catUpdate.mutate({ id: editingCategory.id, dto });
          } else {
            catCreate.mutate(dto);
          }
        }}
      />

      {/* Discipline edit/create */}
      <DisciplineDialog
        open={!!discDialog}
        discipline={discDialog?.discipline ?? null}
        loading={discSave.isPending}
        onCancel={() => setDiscDialog(null)}
        onSubmit={(dto) =>
          discDialog && discSave.mutate({
            categoryId: discDialog.categoryId,
            id: discDialog.discipline?.id ?? null,
            dto,
          })
        }
      />

      <ConfirmDialog
        open={!!deletingCategory}
        title="Smazat kategorii?"
        message={
          deletingCategory
            ? (() => {
                const resCount = countResultsForCategory(deletingCategory);
                const teams = deletingCategory.teamCount;
                const discs = deletingCategory.disciplines.length;
                const base = `Kategorie "${deletingCategory.name}" bude trvale smazána.`;
                if (resCount > 0 || teams > 0) {
                  const parts: string[] = [];
                  if (discs > 0) parts.push(`${discs} disciplín`);
                  if (teams > 0) parts.push(`${teams} družstev`);
                  if (resCount > 0) parts.push(`${resCount} zapsaných výsledků`);
                  return `⚠️ ${base} Ztratíte: ${parts.join(', ')}. Tato akce je nevratná.`;
                }
                return base;
              })()
            : ''
        }
        destructive
        loading={catDelete.isPending}
        onCancel={() => setDeletingCategory(null)}
        onConfirm={() => deletingCategory && catDelete.mutate(deletingCategory.id)}
      />

      <ConfirmDialog
        open={!!deletingDiscipline}
        title="Smazat disciplínu?"
        message={
          deletingDiscipline
            ? (() => {
                const resCount = countResultsForDiscipline(deletingDiscipline.id);
                const base = `Disciplína "${deletingDiscipline.name}" bude trvale smazána.`;
                if (resCount > 0) {
                  return `⚠️ ${base} Ztratíte ${resCount} zapsaných výsledků. Tato akce je nevratná.`;
                }
                return base;
              })()
            : ''
        }
        destructive
        loading={discDelete.isPending}
        onCancel={() => setDeletingDiscipline(null)}
        onConfirm={() => deletingDiscipline && discDelete.mutate(deletingDiscipline.id)}
      />

      {/* Start list groups dialog */}
      <Dialog
        open={!!startListDialog}
        onClose={() => setStartListDialog(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Startovka</DialogTitle>
        <DialogContent>
          {startListDialog && (
            <Box sx={{ pt: 1 }}>
              <Tabs
                value={startListDialogTab}
                onChange={(_, v) => setStartListDialogTab(v as 'config' | 'generated')}
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab value="config" label="Konfigurace skupin" />
                <Tab value="generated" label="Vygenerovana startovka" />
              </Tabs>
              {startListDialogTab === 'config' && (
                <StartListConfigTab
                  disciplineId={startListDialog.id}
                  disciplineName={startListDialog.name}
                  isIndividual={!!startListDialog.isIndividual}
                />
              )}
              {startListDialogTab === 'generated' && (
                <StartListTab
                  disciplineId={startListDialog.id}
                  disciplineName={startListDialog.name}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartListDialog(null)}>Zavřít</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface CategoryDialogProps {
  open: boolean;
  category: Category | null;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (dto: CreateCategoryDto) => void;
}

const AGE_GROUPS: AgeGroup[] = ['MLADSI_ZACI', 'STARSI_ZACI', 'DOROSTENCI', 'DOROSTENKY', 'MUZI', 'ZENY', 'OSTATNI'];

const CategoryDialog = ({ open, category, loading, onCancel, onSubmit }: CategoryDialogProps) => {
  const [name, setName] = useState('');
  const [ageGroup, setAgeGroup] = useState<AgeGroup>('OSTATNI');
  const [sortOrder, setSortOrder] = useState<number>(0);

  const handleEnter = () => {
    if (category) {
      setName(category.name);
      setAgeGroup(category.ageGroup);
      setSortOrder(category.sortOrder);
    } else {
      setName('');
      setAgeGroup('OSTATNI');
      setSortOrder(0);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth TransitionProps={{ onEnter: handleEnter }}>
      <DialogTitle>{category ? 'Upravit kategorii' : 'Nová kategorie'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Název" value={name} onChange={(e) => setName(e.target.value)} required autoFocus fullWidth />
          <FormControl fullWidth>
            <InputLabel>Věková kategorie</InputLabel>
            <Select
              value={ageGroup}
              label="Věková kategorie"
              onChange={(e) => setAgeGroup(e.target.value as AgeGroup)}
            >
              {AGE_GROUPS.map((g) => (
                <MenuItem key={g} value={g}>
                  {AGE_GROUP_LABELS[g]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Pořadí zobrazení"
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
            inputProps={{ min: 0 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>Zrušit</Button>
        <Button variant="contained" onClick={() => onSubmit({ name, ageGroup, sortOrder })} disabled={loading || !name}>
          {category ? 'Uložit' : 'Vytvořit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface DisciplineDialogProps {
  open: boolean;
  discipline: Discipline | null;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (dto: CreateDisciplineDto) => void;
}

const DISCIPLINE_TYPES: DisciplineType[] = [
  'POZARNI_UTOK',
  'STAFETA_DVOJIC',
  'STAFETA_4X60',
  'STAFETA_4X100',
  'BEH_60M_PREKAZKY',
  'BEH_100M_PREKAZKY',
  'BEH_100M_PHP',
  'POZARNI_UTOK_CTIF',
  'STAFETA_CTIF',
  'VEZ',
];

const DisciplineDialog = ({ open, discipline, loading, onCancel, onSubmit }: DisciplineDialogProps) => {
  const [name, setName] = useState('');
  const [type, setType] = useState<DisciplineType>('POZARNI_UTOK');
  const [attemptsCount, setAttemptsCount] = useState(1);
  const [sortOrder, setSortOrder] = useState(0);
  const [timeFormat, setTimeFormat] = useState<TimeFormat>('MM_SS_CC');
  const [useSecondTimer, setUseSecondTimer] = useState(false);
  const [isIndividual, setIsIndividual] = useState(false);
  const [teamMaxRunners, setTeamMaxRunners] = useState<string>('');
  const [teamScoringCount, setTeamScoringCount] = useState<string>('');
  const [teamMinRequired, setTeamMinRequired] = useState<string>('');

  const handleEnter = () => {
    if (discipline) {
      setName(discipline.name);
      setType(discipline.type);
      setAttemptsCount(discipline.attemptsCount);
      setSortOrder(discipline.sortOrder);
      setTimeFormat(discipline.timeFormat ?? 'MM_SS_CC');
      setUseSecondTimer(!!discipline.useSecondTimer);
      setIsIndividual(!!discipline.isIndividual);
      setTeamMaxRunners(discipline.teamMaxRunners != null ? String(discipline.teamMaxRunners) : '');
      setTeamScoringCount(discipline.teamScoringCount != null ? String(discipline.teamScoringCount) : '');
      setTeamMinRequired(discipline.teamMinRequired != null ? String(discipline.teamMinRequired) : '');
    } else {
      setName('Požární útok');
      setType('POZARNI_UTOK');
      setAttemptsCount(1);
      setSortOrder(0);
      setTimeFormat('SS_CC'); // PU je default, < 60s => SS,cc
      setUseSecondTimer(false);
      setIsIndividual(false);
      setTeamMaxRunners('');
      setTeamScoringCount('');
      setTeamMinRequired('');
    }
  };

  useEffect(() => {
    if (!discipline) {
      if (type === 'BEH_100M_PREKAZKY' || type === 'BEH_100M_PHP' || type === 'VEZ') {
        setIsIndividual(true);
        setTimeFormat('SS_CC');
      } else if (type === 'BEH_60M_PREKAZKY') {
        setIsIndividual(false);
        setTimeFormat('SS_CC');
      } else if (type === 'STAFETA_4X100') {
        setIsIndividual(false);
        setTimeFormat('MM_SS_CC');
      } else {
        setIsIndividual(false);
        // PÚ, štafeta dvojic, štafeta 4×60 i CTIF se ve výsledkových listinách Plamen
        // píší v sekundách (i nad 60 s, např. 64,916). Default = SS_CC.
        setTimeFormat('SS_CC');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth TransitionProps={{ onEnter: handleEnter }}>
      <DialogTitle>{discipline ? 'Upravit disciplínu' : 'Nová disciplína'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Typ disciplíny</InputLabel>
            <Select
              value={type}
              label="Typ disciplíny"
              onChange={(e) => {
                const newType = e.target.value as DisciplineType;
                setType(newType);
                // Auto-nastavit defaultní název podle typu, pokud je current name default nebo empty
                if (!name || DISCIPLINE_TYPES.some((t) => DISCIPLINE_TYPE_LABELS[t] === name)) {
                  setName(DISCIPLINE_TYPE_LABELS[newType]);
                }
              }}
            >
              {DISCIPLINE_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {DISCIPLINE_TYPE_LABELS[t]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Název" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
          <TextField
            label="Počet pokusů"
            type="number"
            value={attemptsCount}
            onChange={(e) => setAttemptsCount(Math.max(1, Number(e.target.value) || 1))}
            inputProps={{ min: 1, max: 5 }}
            helperText={
              useSecondTimer
                ? '1 nebo 2 pokusy. 2 časoměřiči: bere se horší z jejich časů.'
                : '1 nebo 2 pokusy. Z více pokusů se bere lepší.'
            }
          />
          <TextField
            label="Pořadí zobrazení"
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
            inputProps={{ min: 0 }}
          />
          <FormControl fullWidth>
            <InputLabel>Formát času</InputLabel>
            <Select
              value={timeFormat}
              label="Formát času"
              onChange={(e) => setTimeFormat(e.target.value as TimeFormat)}
            >
              <MenuItem value="SS_CC">SS,cc — sekundy i nad 60 s (PÚ, běhy, věž, štafeta dvojic, 4×60, CTIF)</MenuItem>
              <MenuItem value="MM_SS_CC">M:SS,cc — dlouhá štafeta dorost/PS (4×100)</MenuItem>
            </Select>
          </FormControl>
          <Divider sx={{ my: 1 }}>Individuální disciplína</Divider>

          <FormControlLabel
            control={
              <Checkbox
                checked={isIndividual}
                onChange={(e) => setIsIndividual(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Individuální disciplína</Typography>
                <Typography variant="caption" color="text.secondary">
                  Závodníci běží za sebou; výsledky se vedou na osobu, ne na družstvo (běh 100m / 60m / věž).
                </Typography>
              </Box>
            }
          />

          {isIndividual && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Max závodníků/družstvo"
                type="number"
                value={teamMaxRunners}
                onChange={(e) => setTeamMaxRunners(e.target.value)}
                helperText="Prázdné = bez družstevního hodnocení"
                inputProps={{ min: 1, max: 20 }}
                fullWidth
              />
              <TextField
                label="Sčítá se nejlepších"
                type="number"
                value={teamScoringCount}
                onChange={(e) => setTeamScoringCount(e.target.value)}
                helperText="Např. 6 (PS), 4 (okres/dorost)"
                inputProps={{ min: 1, max: 20 }}
                fullWidth
              />
              <TextField
                label="Min. validních"
                type="number"
                value={teamMinRequired}
                onChange={(e) => setTeamMinRequired(e.target.value)}
                helperText="Méně = tým nehodnocen"
                inputProps={{ min: 1, max: 20 }}
                fullWidth
              />
            </Stack>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={useSecondTimer}
                onChange={(e) => setUseSecondTimer(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Zapisovat 2. časoměřič</Typography>
                <Typography variant="caption" color="text.secondary">
                  Pokud zaškrtnuto, zapisovatel zadává 2 časy — bere se horší z nich (2 časoměřiči).
                </Typography>
              </Box>
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>Zrušit</Button>
        <Button
          variant="contained"
          onClick={() =>
            onSubmit({
              name,
              type,
              attemptsCount,
              sortOrder,
              timeFormat,
              useSecondTimer,
              isIndividual,
              teamMaxRunners: teamMaxRunners ? Number(teamMaxRunners) : null,
              teamScoringCount: teamScoringCount ? Number(teamScoringCount) : null,
              teamMinRequired: teamMinRequired ? Number(teamMinRequired) : null,
            })
          }
          disabled={loading || !name}
        >
          {discipline ? 'Uložit' : 'Vytvořit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
